// src/js/constants/seo-config.js
const SITE_NAME = 'Neurochecklists';
const SITE_URL = 'https://neurochecklists.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.png`; // 680x680px
const TWITTER_HANDLE = '@neurochecklists';

const SEO_CONFIG = {
  homepage: {
    title: 'Neurochecklists | Evidence-Based Neurology Checklists for Clinicians & Students',
    // same text as the server-rendered description in src/index.tpl.html
    description: 'Access thousands of fully referenced neurology checklists for clinicians, medical students, and healthcare professionals.',
    canonical: SITE_URL,
    ogType: 'website',
    keywords: 'neurology checklists, clinical neurology, neurology reference, medical checklists, neurology students, neurology diagnosis'
  },
  explore: {
    title: 'Explore Neurology Topics & Checklists | Neurochecklists',
    description: 'Browse our complete database of neurology checklists organised by subspecialty. Covers stroke, epilepsy, movement disorders, headache, neuromuscular diseases, and more.',
    canonical: `${SITE_URL}/explore`,
    ogType: 'website',
    keywords: 'neurology topics, neurology subspecialties, neurology database, clinical checklists'
  },
  pricing: {
    title: 'Pricing & Plans | Neurochecklists — 7-Day Free Trial',
    description: 'Try every checklist free for 7 days, no credit card required. Premium access from £4.99/month, with student discounts for valid institutional email addresses. Annual plans save 17%.',
    canonical: `${SITE_URL}/pricing`,
    ogType: 'website',
    keywords: 'neurochecklists pricing, medical app subscription, student discount neurology'
  },
  about: {
    title: 'About Neurochecklists | Evidence-Based Neurology Database',
    description: 'Neurochecklists is a comprehensive, evidence-based neurology checklist database developed for clinicians, students, and patients. Covering all neurological subspecialties with fully referenced content.',
    canonical: `${SITE_URL}/about`,
    ogType: 'website',
    keywords: 'about neurochecklists, neurology database, evidence-based neurology'
  },
  ibrahimImam: {
    title: 'Dr Ibrahim Imam, Consultant Neurologist | Neurochecklists',
    description: 'Dr Ibrahim Imam MBBS, FRCP is a consultant neurologist at Beacon Hospital, Dublin, and the author and reviewer of every Neurochecklists checklist.',
    canonical: `${SITE_URL}/about/ibrahim-imam`,
    ogType: 'profile'
  },
  axon: {
    title: 'Axon AI Neurology Assistant | Evidence-Grounded Answers',
    description: 'Meet Axon, the Neurochecklists AI neurology assistant. Ask clinical questions and receive structured answers grounded in a fully referenced neurology knowledge base.',
    canonical: `${SITE_URL}/axon`,
    ogType: 'website',
    ogImage: `${SITE_URL}/images/axon-ai-neurology-assistant.png`,
    ogImageAlt: 'Axon AI neurology assistant connecting clinical questions with referenced neurology knowledge',
    ogImageWidth: 1536,
    ogImageHeight: 1024,
    keywords: 'AI neurology assistant, neurology AI, clinical neurology assistant, evidence based medical AI, Axon AI, Neurochecklists AI'
  },
  contact: {
    title: 'Contact Us | Neurochecklists',
    description: 'Get in touch with the Neurochecklists team. We welcome feedback, partnership enquiries, institutional licensing questions, and support requests.',
    canonical: `${SITE_URL}/contact`,
    ogType: 'website'
  },
  faqs: {
    title: 'Frequently Asked Questions | Neurochecklists',
    description: 'Find answers to common questions about Neurochecklists including account setup, subscriptions, student discounts, institutional access, and how to use the platform.',
    canonical: `${SITE_URL}/faqs`,
    ogType: 'website'
  },
  search: {
    // Dynamic — title set per query
    titleTemplate: (query) => `Search results for "${query}" | Neurochecklists`,
    description: 'Search thousands of fully referenced neurology checklists. Find clinical information across all neurological subspecialties.',
    ogType: 'website'
  },
  surfingTheBrain: {
    title: 'Surfing the Brain — A Neurology Book | Neurochecklists',
    description: 'Purchase "Surfing the Brain" — a comprehensive neurology guide. Available for £9.99 with secure online checkout.',
    canonical: `${SITE_URL}/surfing-the-brain`,
    ogType: 'product'
  },
  // Dynamic pages (checklists)
  checklist: {
    titleTemplate: (name) => `${name} | Neurology Checklist | Neurochecklists`,
    ogType: 'article'
  },
  // Legal pages
  privacy: {
    title: 'Privacy Policy | Neurochecklists',
    description: 'Read the Neurochecklists privacy policy. Learn how we collect, use, and protect your personal data in compliance with GDPR.',
    canonical: `${SITE_URL}/privacy-policy`
  },
  data: {
    title: 'Data Policy | Neurochecklists',
    description: 'Neurochecklists data policy. Information about data collection, storage, and your rights under data protection regulations.',
    canonical: `${SITE_URL}/data-policy`
  },
  tcs: {
    title: 'Terms & Conditions | Neurochecklists',
    description: 'Terms and conditions for using the Neurochecklists platform. Covers account usage, subscription terms, and intellectual property.',
    canonical: `${SITE_URL}/terms-and-conditions`
  },
  credits: {
    title: 'Site Credits | Neurochecklists',
    description: 'Acknowledgements and credits for the Neurochecklists platform.',
    canonical: `${SITE_URL}/credits`
  },
  fourOhFour: {
    title: 'Page Not Found | Neurochecklists',
    description: 'The page you are looking for could not be found. Try searching our neurology checklists or browsing by topic.',
    noindex: true
  },
  // Audience pages (NEW — Section 3.4)
  students: {
    title: 'Neurology Checklists for Medical Students | Neurochecklists',
    description: 'The essential neurology revision tool for medical students. Access thousands of referenced checklists, get student discounts, and prepare for exams with evidence-based content.',
    canonical: `${SITE_URL}/for/students`,
    ogType: 'website',
    keywords: 'medical student neurology, neurology revision, neurology exam preparation, medical student resources'
  },
  patients: {
    title: 'Neurology Information for Patients & Carers | Neurochecklists',
    description: 'Understand your neurological condition with trusted, evidence-based checklists. Written for patients and carers to help navigate diagnosis, treatment, and management.',
    canonical: `${SITE_URL}/for/patients`,
    ogType: 'website',
    keywords: 'neurology patient information, neurological conditions, patient education neurology'
  },
  healthcareProfessionals: {
    title: 'Clinical Neurology Checklists for Healthcare Professionals | Neurochecklists',
    description: 'Point-of-care neurology checklists for clinicians, GPs, and allied health professionals. Evidence-based, fully referenced, and regularly updated. Streamline your clinical decision-making.',
    canonical: `${SITE_URL}/for/healthcare-professionals`,
    ogType: 'website',
    keywords: 'clinical neurology checklist, point of care neurology, GP neurology reference, clinical decision support'
  },
  lecturers: {
    title: 'Neurology Teaching Resources for Lecturers & Educators | Neurochecklists',
    description: 'Enhance your neurology teaching with downloadable, fully referenced checklists. Ideal for medical school lectures, tutorials, and curriculum development. Institutional licensing available.',
    canonical: `${SITE_URL}/for/lecturers`,
    ogType: 'website',
    keywords: 'neurology teaching resources, medical education neurology, neurology lecture materials, medical school curriculum'
  },
  organisations: {
    title: 'Institutional & Enterprise Neurology Access | Neurochecklists',
    description: 'Neurochecklists for hospitals, universities, and healthcare organisations. Group licensing, institutional discounts, and admin dashboards. Equip your team with evidence-based neurology tools.',
    canonical: `${SITE_URL}/for/organisations`,
    ogType: 'website',
    keywords: 'institutional neurology access, hospital neurology tools, enterprise medical checklists, group licensing neurology'
  }
};

export { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE, TWITTER_HANDLE, SEO_CONFIG };
